import sanitizeHtml from 'sanitize-html';

import { getPreparedPreloadImage } from 'utils/image';
import { hexToRGB } from 'utils/helper/common';
import { logError } from 'utils/log';
import { getCloudinaryImageUrl } from 'utils/cloudinary';
import { getTooltip } from './network-graph-tooltip';

import type {
  NetworkGraphData,
  NetworkGraphDataRaw,
  NetworkGraphEdge,
  NetworkGraphEdgeRaw,
  NetworkGraphNode,
  NetworkGraphNodeRaw,
  NetworkGraphTierLabels,
  NetworkGraphTiers
} from 'types';

const SANITIZE_HTML_CONFIG = {
  allowedTags: [],
  allowedAttributes: {}
};

export const TIERS: Array<NetworkGraphTierLabels> = [
  'stars',
  'giga',
  'mega',
  'macro',
  'medium',
  'micro',
  'nano'
];

export const TIERS_MAP: NetworkGraphTiers = {
  stars: { min: 1000000, max: 10000000000 }, // this is 2.5 billion more than all people in the world :)
  giga: { min: 500000, max: 999999 },
  mega: { min: 250000, max: 499999 },
  macro: { min: 100000, max: 249999 },
  medium: { min: 50000, max: 99999 },
  micro: { min: 20000, max: 49999 },
  nano: { min: 1, max: 19999 }
};

export const SYMBOL_SIZES = [210, 66, 28];
export const FULL_IMAGE_SIZE = 500;
export const COMPACT_IMAGE_SIZE = 320;

const COLOR_PRIMARY = '#00b78b';
const COLOR_LIGHT_GREY = '#c5c5c5';
const COLOR_DARK_GREY = '#666';

export const COLORS = [COLOR_LIGHT_GREY, COLOR_PRIMARY, COLOR_DARK_GREY];

export const getProfileImageUrl = (profileImage: string, imageSize: number): string => {
  const image = {
    url: profileImage
  };

  return getCloudinaryImageUrl(image, {
    width: imageSize,
    height: imageSize
  });
};

export const getTier = (followers: number): NetworkGraphTierLabels => {
  for (let i = 0; i < TIERS.length; i++) {
    const tier = TIERS[i];
    const range = TIERS_MAP[tier];

    if (followers > range.min && followers < range.max) {
      return tier;
    }
  }

  return 'nano';
};

export const getPreparedEdges = (edges: Array<NetworkGraphEdgeRaw>): Array<NetworkGraphEdge> => (
  edges.map(item => ({
    from: Number(item.source),
    to: Number(item.target)
  }))
);

const COLLABARY_IMAGE_REGEX = /collabary-images/;

function getImageSize(profileImageUrl?: string | null): number {
  if (profileImageUrl?.match(COLLABARY_IMAGE_REGEX)) {
    return FULL_IMAGE_SIZE;
  }

  return COMPACT_IMAGE_SIZE;
}

export const getPreparedNodes = (nodes: Array<NetworkGraphNodeRaw>): Array<NetworkGraphNode> => (
  nodes.map(item => {
    const symbolImageUrl = getProfileImageUrl(item.profileImage, getImageSize(item.profileImage));
    const symbolSizeIndex = item.symbolSize - 1;
    const tier = getTier(item.followers);

    // preload images so the off-screen nodes load immediately when scrolling into view
    getPreparedPreloadImage({
      assetUrl: symbolImageUrl,
      code: item.id,
      type: 'instagram',
      url: ''
    }, symbolImageUrl);

    const borderColorRGB = hexToRGB(COLORS[symbolSizeIndex]);

    const sanitizedItem = {
      ...item,
      profileName: sanitizeHtml(item.profileName, SANITIZE_HTML_CONFIG)
    };

    return {
      id: Number(sanitizedItem.id),
      image: symbolImageUrl,
      size: SYMBOL_SIZES[symbolSizeIndex],
      level: symbolSizeIndex,
      title: getTooltip(sanitizedItem, tier),
      color: {
        border: `rgba(${borderColorRGB.r}, ${borderColorRGB.g}, ${borderColorRGB.b}, 0.7)`
      }
    };
  })
);


// Nodes coordinate calculation
const INITIAL_ROTATION_SHIFT = Math.PI / 4; // tune this to rotate first level nodes if needed
const FIRST_LEVEL_NODE_PADDING = 50; // first level node padding, change to add/reduce space around nodes
const MIN_FIRST_LEVEL_NODE_DISTANCE = SYMBOL_SIZES[0] * 2; // twice of the node size
const MIN_SECOND_LEVEL_NODE_DISTANCE = SYMBOL_SIZES[1] * 2; // twice of the node size

type SecondLevelIds = {
  [x: string]: Array<number>
};

function getSecondNodeRequiredSpace(rotationStep: number): number {
  return Math.max(
    MIN_SECOND_LEVEL_NODE_DISTANCE,
    SYMBOL_SIZES[2] / Math.sin(rotationStep / 2)
  );
}

// Used to calculate max distance between *far most edges* of the second level nodes
// -->(2)--(  1  )--(2)<--
function getSecondLevelMaxDist(secondLevelIds: SecondLevelIds): number {
  const maxSecondLevelNodes = Object.keys(secondLevelIds)
    .reduce((max: number, key: string) => {
      const ids = secondLevelIds[key];
      const size = ids.length;
      return size > max ? size : max;
    }, 0);

  if (maxSecondLevelNodes === 0) {
    // if no second level nodes, return size of the node
    return SYMBOL_SIZES[1];
  }

  const maxSecondLevelRotationStep = 2 * Math.PI / (maxSecondLevelNodes + 1);
  const distanceBetweenCenters = getSecondNodeRequiredSpace(maxSecondLevelRotationStep);

  return distanceBetweenCenters + SYMBOL_SIZES[2];
}

function collectFirstLevelNodePositions(
  nodes: Array<NetworkGraphNode>,
  firstLevelIds: Array<number>,
  secondLevelIds: SecondLevelIds
): {
  [x: string]: {
    x: number,
    y: number
  }
} {
  const parentCoords: {
    [x: string]: {
      x: number,
      y: number
    }
  } = {};
  const firstLevelRotationStep = 2 * Math.PI / firstLevelIds.length;
  const maxRequiredSpace = getSecondLevelMaxDist(secondLevelIds) + FIRST_LEVEL_NODE_PADDING;

  // flag to show if we can render first level nodes in two levels
  // determined based on distance from central node
  let canVariate = false;

  // will iterate through first level nodes and calculate positions for them
  nodes.forEach((node) => {
    if (node.level !== 1) {
      return;
    }

    const firstLevelIndex = firstLevelIds.indexOf(node.id);
    if (firstLevelIndex === -1) {
      // shouldn't happen, but let's check
      return;
    }

    let dist = Math.max(
      MIN_FIRST_LEVEL_NODE_DISTANCE,
      maxRequiredSpace / Math.sin(firstLevelRotationStep / 2)
    );

    if (dist > maxRequiredSpace * 2.2 + SYMBOL_SIZES[0]) {
      // first level nodes are far enough from the center
      // so we can variate distances for them in order to pack better
      canVariate = true;
    }

    if (canVariate && firstLevelIndex % 2 === 1) {
      // variate distance for each second node
      dist *= 0.7;
    }

    const x = Math.cos(firstLevelIndex * firstLevelRotationStep + INITIAL_ROTATION_SHIFT) * dist;
    const y = Math.sin(firstLevelIndex * firstLevelRotationStep + INITIAL_ROTATION_SHIFT) * dist;

    parentCoords[String(node.id)] = { x, y };
  });

  return parentCoords;
}

export function distributeNodes(nodes: Array<NetworkGraphNode>, edges: Array<NetworkGraphEdge>): Array<NetworkGraphNode> {
  const firstLevelIds: Array<number> = [];
  const secondLevelIds: SecondLevelIds = {};

  // collect all first level and second level nodes
  nodes.forEach((node) => {
    if (node.level === 1) {
      firstLevelIds.push(node.id);
    }
    if (node.level === 2) {
      const parent = edges.find((edge) => edge.from === node.id);
      if (parent) {
        secondLevelIds[String(parent.to)] = [
          ...(secondLevelIds[String(parent.to)] || []),
          node.id
        ];
      }
    }
  });

  const parentCoords = collectFirstLevelNodePositions(nodes, firstLevelIds, secondLevelIds);

  return nodes.map((node) => {
    let x = 0;
    let y = 0;

    if (node.level === 1) {
      // use pre-calculated
      x = parentCoords[String(node.id)].x;
      y = parentCoords[String(node.id)].y;
    }

    if (node.level === 2) {
      const parentKey = Object.keys(secondLevelIds)
        .find((id) => secondLevelIds[id].includes(node.id));

      if (!parentKey || !parentCoords[parentKey]) {
        logError('Can not find parent key for second level node', node);
        return node;
      }

      // we need to calculate step for N+1 nodes in order to have free space for parent node edge
      const secondLevelRotationStep = 2 * Math.PI / (secondLevelIds[parentKey].length + 1);
      // initial angle is based on parent node coordinates to keep edge uncovered
      const initialAngle = Math.atan2(parentCoords[parentKey].y, parentCoords[parentKey].x) + Math.PI;
      const secondLevelIndex = secondLevelIds[parentKey].indexOf(node.id) + 1;

      const dist = getSecondNodeRequiredSpace(secondLevelRotationStep);
      x = Math.cos(initialAngle + secondLevelIndex * secondLevelRotationStep) * dist + parentCoords[parentKey].x;
      y = Math.sin(initialAngle + secondLevelIndex * secondLevelRotationStep) * dist + parentCoords[parentKey].y;
    }

    return { ...node, x, y };
  });
}

export const getPreparedData = (data: NetworkGraphDataRaw): NetworkGraphData => {
  const edges = getPreparedEdges(data.links);
  const preparedNodes = getPreparedNodes(data.nodes);
  const nodes = distributeNodes(preparedNodes, edges);

  return ({
    nodes,
    edges
  });
};

export const getData = (data: NetworkGraphDataRaw): NetworkGraphData => (
  getPreparedData(data)
);
